import type { AddStepPresetId, WorkflowStepType, WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Segmented } from "@sagot/ui";
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { CanvasView } from "../canvas/canvas-view";
import { prefersReducedMotion } from "../canvas/motion";
import { PathJumpBar } from "../canvas/path-jump-bar";
import { addPath, addStep, freshStepId, kindOf, materializeNewPaths, moveStep, nextPathId, pathChoices, removeStep, replaceStep, stepIds } from "../definition-model";
import { NO_PROBLEMS, type ProblemIndex } from "../problem-model";
import type { Definition, Step } from "../types";
import type { OnEdit } from "../use-definition-history";
import { createStep, replaceLastStep } from "./add-step/replace-last-step";
import { BuilderToolsContext } from "./builder-tools";
import type { FlowContext } from "./flow-context";
import { HistoryButtons } from "./history-buttons";
import { useHistory } from "./history-context";
import { HookKeysContext, hookKeysOf, RunVariablesContext, runVariablesOf, TriggerContext } from "./hook-model";
import { usePathLimit } from "./system/path-limit";
import { NotesStrip } from "./notes-strip";
import { PathCard } from "./path-card";
import { ProblemsContext } from "./problems-context";
import { SettingsCard } from "./settings-card";
import { StepSheet } from "./step-sheet";
import { TriggerSheet } from "./trigger-sheet";
import { useBuilderToolsValue } from "./use-builder-tools";

interface WorkflowBuilderProps {
  trigger: WorkflowTrigger;
  definition: Definition;
  disabled: boolean;
  /** What is wrong with the workflow, to mark on the paths, steps and exits; none by default. */
  problems?: ProblemIndex;
  onChange: OnEdit;
}

type View = "list" | "canvas";

/** The workflow as a list (the trigger card, then each path with its steps; keyboard-friendly) or as a canvas of path tiles; both edit the same definition. */
export function WorkflowBuilder({ trigger, definition, disabled, problems = NO_PROBLEMS, onChange }: WorkflowBuilderProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const [view, setView] = useState<View>("list");
  const [editing, setEditing] = useState<{ path: string; index: number } | null>(null);
  const [triggerOpen, setTriggerOpen] = useState(false);
  // The path last jumped to (the jump bar marks it), and a path the view should bring into sight once it has drawn it.
  const [current, setCurrent] = useState<string | null>(null);
  const [reveal, setReveal] = useState<string | null>(null);
  // The control that opened the step sheet: focus goes back to it when the sheet closes (WCAG 2.4.3), as a keyboard user left it.
  const opener = useRef<HTMLElement | null>(null);
  // An undo or a redo changes what the sheet was showing: it closes rather than showing a version that is gone.
  const epoch = history?.epoch ?? 0;
  const [seenEpoch, setSeenEpoch] = useState(epoch);
  if (seenEpoch !== epoch) {
    setSeenEpoch(epoch);
    setEditing(null);
  }
  const openEditor = (at: { path: string; index: number }) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setEditing(at);
  };
  const closeEditor = () => {
    setEditing(null);
    const target = opener.current;
    opener.current = null;
    if (target) window.setTimeout(() => { if (target.isConnected) target.focus(); }, 0);
  };
  const pathIds = Object.keys(definition.paths).sort();
  const taken = stepIds(definition);
  const editingStep = editing ? definition.paths[editing.path]?.steps[editing.index] : undefined;
  const settingsChange = (settings: Definition["settings"]) => { onChange({ ...definition, settings }, { coalesceKey: "settings" }); };
  const hasFacing = kindOf(definition) === "facing";
  const hookKeys = useMemo(() => hookKeysOf(definition), [definition]);
  const runVariables = useMemo(() => runVariablesOf(definition), [definition]);
  const tools = useBuilderToolsValue({ definition, disabled, onChange, onReveal: setReveal });
  const pathLimit = usePathLimit(trigger);

  const add = (pathId: string, type: WorkflowStepType, preset?: AddStepPresetId) => {
    const firstOther = pathChoices(definition, pathId)[0] ?? pathId;
    const next = addStep(definition, pathId, createStep({ type, preset }, freshStepId(type, taken), firstOther));
    onChange(next);
    if (type !== "end") setEditing({ path: pathId, index: definition.paths[pathId]?.steps.length ?? 0 });
  };
  /** "Replace with" in the Add-step menu: the last step of the path is swapped for another, and the new one opens. */
  const replace = (pathId: string, type: WorkflowStepType, preset?: AddStepPresetId) => {
    const replaced = replaceLastStep(definition, pathId, { type, preset });
    if (!replaced) return;
    onChange(replaced.definition);
    if (type !== "end") setEditing({ path: pathId, index: replaced.index });
  };
  /** An edited step may ask for new paths ("New path" in a list of exits): create them with the change. Edits of one step within a moment are one undo. */
  const editStep = (at: { path: string; index: number }, step: Step) => {
    const resolved = materializeNewPaths(definition, step);
    onChange(replaceStep(resolved.definition, at.path, at.index, resolved.step), { coalesceKey: `step:${step.id}` });
  };
  const flowFor = (pathId: string, index: number): FlowContext => ({
    paths: pathChoices(definition, pathId),
    taken,
    agentIds: definition.settings.agent_ids,
    canAddPath: nextPathId(definition) !== null,
    facing: hasFacing,
    pathId,
    following: definition.paths[pathId]?.steps.slice(index + 1) ?? [],
  });
  const jumpInList = (id: string) => {
    const heading = document.getElementById(`path-${id}`);
    if (!heading) return;
    heading.scrollIntoView({ block: "start", behavior: prefersReducedMotion() ? "auto" : "smooth" });
    heading.focus({ preventScroll: true });
    setCurrent(id);
  };
  // A path the author just made (a copy) is brought into sight in the list once the list has drawn it; the canvas does the same by itself.
  const revealInList = useEffectEvent((id: string) => {
    jumpInList(id);
    setReveal(null);
  });
  useEffect(() => {
    if (reveal === null || view !== "list" || !(reveal in definition.paths)) return undefined;
    const frame = window.requestAnimationFrame(() => { revealInList(reveal); });
    return () => { window.cancelAnimationFrame(frame); };
  }, [reveal, view, definition.paths]);
  const addPathButton = (
    <Button type="button" variant="secondary" className="w-full" disabled={disabled || pathIds.length >= pathLimit} onClick={() => { onChange(addPath(definition).definition); }}>
      <Icon name="plus" />
      {t("engage.workflows.builder.addPath")}
    </Button>
  );
  return (
    <ProblemsContext.Provider value={problems}>
      <BuilderToolsContext.Provider value={tools}>
        <TriggerContext.Provider value={trigger}>
          <HookKeysContext.Provider value={hookKeys}>
          <RunVariablesContext.Provider value={runVariables}>
            <div className={view === "list" ? "mx-auto max-w-3xl space-y-4" : "space-y-4"}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Segmented
                  aria-label={t("engage.workflows.canvas.view")}
                  value={view}
                  onValueChange={setView}
                  options={[{ value: "list", label: t("engage.workflows.canvas.list") }, { value: "canvas", label: t("engage.workflows.canvas.canvas") }]}
                />
                {disabled ? null : <HistoryButtons />}
              </div>
              <NotesStrip />
              {view === "list" ? (
                <>
                  <PathJumpBar definition={definition} current={current} onJump={jumpInList} />
                  <SettingsCard trigger={trigger} settings={definition.settings} hasFacing={hasFacing} disabled={disabled} onChange={settingsChange} />
                  {pathIds.map((id) => {
                    const path = definition.paths[id];
                    if (!path) return null;
                    return (
                      <PathCard
                        key={id}
                        id={id}
                        path={path}
                        trigger={trigger}
                        disabled={disabled}
                        onAdd={(type, preset) => { add(id, type, preset); }}
                        onReplace={(type, preset) => { replace(id, type, preset); }}
                        onEdit={(index) => { openEditor({ path: id, index }); }}
                        onMove={(from, to) => { onChange(moveStep(definition, id, from, to)); }}
                        onRemove={(index) => { onChange(removeStep(definition, id, index)); }}
                      />
                    );
                  })}
                  {addPathButton}
                </>
              ) : (
                <CanvasView
                  trigger={trigger}
                  definition={definition}
                  disabled={disabled}
                  reveal={reveal}
                  onRevealed={() => { setReveal(null); }}
                  onEdit={(path, index) => { openEditor({ path, index }); }}
                  onAdd={add}
                  onReplace={replace}
                  onAddPath={() => { onChange(addPath(definition).definition); }}
                  onOpenTrigger={() => { setTriggerOpen(true); }}
                  onChange={onChange}
                />
              )}
              {editing && editingStep ? (
                <StepSheet
                  step={editingStep}
                  flow={flowFor(editing.path, editing.index)}
                  disabled={disabled}
                  onChange={(step) => { editStep(editing, step); }}
                  onClose={closeEditor}
                />
              ) : null}
              {triggerOpen ? (
                <TriggerSheet trigger={trigger} settings={definition.settings} hasFacing={hasFacing} disabled={disabled} onChange={settingsChange} onClose={() => { setTriggerOpen(false); }} />
              ) : null}
            </div>
          </RunVariablesContext.Provider>
          </HookKeysContext.Provider>
        </TriggerContext.Provider>
      </BuilderToolsContext.Provider>
    </ProblemsContext.Provider>
  );
}
